import { useContext, useRef, useEffect, useMemo } from "react"
import { Button } from "../../../../button"
import s from "./checkbox-tree-parent.module.scss"
import { MultiSelectContext } from "../../multi-select-context"
import { components } from "react-select"
import Badge from "../../../../badge/badge"
import cn from "classnames"

const CheckboxTreeParentOption = (props) => {
  const { data } = props
  const { state, dispatch } = useContext(MultiSelectContext)
  const { expandedCheckboxParents } = state
  const isExpanded = expandedCheckboxParents.includes(data.label)
  const checkboxRef = useRef(null)
  const childOptions = useMemo(() => Array.isArray(data.options) ? data.options : [], [data.options])
  const childValues = childOptions.map(opt => opt.value)
  const selectedValues = props.selectProps && Array.isArray(props.selectProps.value)
    ? props.selectProps.value.map(opt => opt.value)
    : []
  const numSelected = childValues.filter(val => selectedValues.includes(val)).length
  const isIndeterminate = numSelected > 0 && numSelected < childValues.length
  const isParentChecked = childValues.length > 0 && numSelected === childValues.length

  const inputValue = props.selectProps && typeof props.selectProps.inputValue === "string"
    ? props.selectProps.inputValue.trim().toLowerCase()
    : ""
  const prevInputValueRef = useRef(inputValue)

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate
    }
  }, [isIndeterminate])

  // Expand parent if input value matches any child
  useEffect(() => {
    if (prevInputValueRef.current !== inputValue) {
      prevInputValueRef.current = inputValue
      if (inputValue) {
        const hasMatch = childOptions.some(opt => {
          const label = String(opt.label).toLowerCase()
          const value = String(opt.value).toLowerCase()
          return label.includes(inputValue) || value.includes(inputValue)
        })
        if (hasMatch && !isExpanded) {
          dispatch({ type: "setExpandedCheckboxParents", payload: [...expandedCheckboxParents, data.label] })
        }
        // Optionally, collapse if no match and expanded
        if (!hasMatch && isExpanded) {
          dispatch({ type: "setExpandedCheckboxParents", payload: expandedCheckboxParents.filter(l => l !== data.label) })
        }
      }
    }
  }, [inputValue, childOptions, isExpanded, expandedCheckboxParents, data.label, dispatch])

  // Get visible child values based on search input
  const getVisibleChildValues = (childOptions, inputValue) => {
    if (!inputValue) return childOptions.map(opt => opt.value)
    const search = inputValue.trim().toLowerCase()
    return childOptions
      .filter(opt => {
        const label = String(opt.label).toLowerCase()
        const value = String(opt.value).toLowerCase()
        return label.includes(search) || value.includes(search)
      })
      .map(opt => opt.value)
  }

  // Toggle parent expand/collapse
  const toggleExpandedParent = (label) => {
    const newExpandedCheckboxParents = expandedCheckboxParents.includes(label)
      ? expandedCheckboxParents.filter((v) => v !== label)
      : [...expandedCheckboxParents, label]
    dispatch({ type: "setExpandedCheckboxParents", payload: newExpandedCheckboxParents })
  }

  // Handle parent checkbox change
  const handleChange = (e) => {
    const inputValue = props.selectProps && typeof props.selectProps.inputValue === "string"
      ? props.selectProps.inputValue.trim().toLowerCase()
      : ""
    const parentLabel = String(data.label).toLowerCase()
    let visibleChildValues
    let newSelected

    if (e.target.checked && inputValue && parentLabel.includes(inputValue)) {
      visibleChildValues = childOptions.map(opt => opt.value)
    } else {
      visibleChildValues = getVisibleChildValues(childOptions, inputValue)
    }

    const flatOptions = props.selectProps && Array.isArray(props.selectProps.options)
      ? props.selectProps.options.flatMap(opt => Array.isArray(opt.options) ? opt.options : [opt])
      : []

    if (e.target.checked) {
      newSelected = Array.from(new Set([...selectedValues, ...visibleChildValues]))
    } else {
      newSelected = selectedValues.filter(val => !visibleChildValues.includes(val))
    }

    const newSelectedOptions = newSelected
      .map(val => flatOptions.find(opt => opt.value === val))
      .filter(Boolean)

    props.selectProps.onChange && props.selectProps.onChange(newSelectedOptions)
  }

  return (
    <components.GroupHeading
      onClick={() => toggleExpandedParent(data.label)}
      className={cn({ [s["bg-expanded"]]: isExpanded })}
      {...props}
    >
      <div className={s["checkbox-tree-parent-container"]}>
        <Button
          type="ghost-icon"
          className={s["checkbox-tree-parent-dropdown-button"]}
          onClick={() => toggleExpandedParent(data.label)}
          buttonType="button"
        >
          <svg
            height="20"
            width="20"
            viewBox="0 0 20 20"
            aria-hidden="true"
            focusable="false"
            className="css-tj5bde-Svg"
            style={{
              transform: isExpanded ? "none" : "rotate(-90deg)",
              transition: "transform 0.2s",
              color: "rgba(102, 102, 102, 1)",
            }}
          >
            <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
          </svg>
        </Button>
        <input
          ref={checkboxRef}
          type="checkbox"
          checked={isParentChecked}
          onChange={handleChange}
          onClick={e => e.stopPropagation()}
          style={{ cursor: "pointer" }}
        />
        <Badge key={data?.label} status={data?.label} color="#065796" marginNone />
      </div>
    </components.GroupHeading>
  )
}

export default CheckboxTreeParentOption